import React from "react";

export const DRIVER_ROUTE_PREVIEW_DURATION_MS = 10_000;

// Own only the preview's presentation; route selection stays in the portal.
export default function useDriverRoutePreview({ autoPreview = false, onPreviewConsumed, canShowMap }) {
  const [phase, setPhase] = React.useState("closed");
  const [automatic, setAutomatic] = React.useState(false);
  const [mapReady, setMapReady] = React.useState(false);
  const [pageVisible, setPageVisible] = React.useState(() => !document.hidden);
  const buttonRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const controllerRef = React.useRef(null);
  const transitionRef = React.useRef(null);
  const isExpanded = phase === "opening" || phase === "open";
  const isMounted = phase !== "closed";
  const isCountingDown = automatic && phase === "open" && mapReady && pageVisible;

  const keepOpen = React.useCallback(() => setAutomatic(false), []);
  const onControllerReady = React.useCallback((controller) => { controllerRef.current = controller; }, []);
  const onMapRenderMetaChange = React.useCallback((meta) => {
    // Include unavailable-map notices so they can also be dismissed normally.
    setMapReady(Boolean(meta && (meta.rendered || meta.category !== "no-trip")));
  }, []);

  React.useEffect(() => {
    if (!autoPreview) return;
    onPreviewConsumed?.();
    if (!canShowMap) return;
    buttonRef.current?.closest(".driver-current-stop")?.scrollIntoView({ block: "start", behavior: "instant" });
    buttonRef.current?.focus({ preventScroll: true });
    setAutomatic(true);
    setPhase("opening");
  }, [autoPreview, canShowMap, onPreviewConsumed]);

  React.useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  React.useEffect(() => {
    if (!isCountingDown) return;
    const timeout = window.setTimeout(() => {
      setAutomatic(false);
      setPhase("closing");
    }, DRIVER_ROUTE_PREVIEW_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [isCountingDown]);

  React.useLayoutEffect(() => {
    if (phase !== "opening" && phase !== "closing") return;
    const panel = panelRef.current;
    const frame = frameRef.current;
    const button = buttonRef.current;
    if (!panel || !frame || !button) return;
    const opening = phase === "opening";
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finish = () => {
      transitionRef.current = null;
      setPhase(opening ? "open" : "closed");
      if (!opening) setMapReady(false);
    };
    if (media.matches || !panel.animate) {
      finish();
      return;
    }

    const mapRect = panel.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const folded = `translate(${buttonRect.left - mapRect.left}px, ${buttonRect.top - mapRect.top}px) scale(${buttonRect.width / panel.offsetWidth}, ${buttonRect.height / panel.offsetHeight})`;
    const previous = transitionRef.current;
    const timing = { duration: opening ? 620 : 520, easing: "cubic-bezier(0.22, 0.8, 0.2, 1)", fill: "both" };
    const surface = panel.animate([
      { transform: previous?.transform || (opening ? folded : "none"), opacity: previous?.opacity ?? (opening ? 0 : 1) },
      { transform: opening ? "none" : folded, opacity: opening ? 1 : 0 },
    ], timing);
    const height = frame.animate([
      { height: `${previous?.height ?? (opening ? 0 : panel.offsetHeight + 12)}px` },
      { height: `${opening ? panel.offsetHeight + 12 : 0}px` },
    ], timing);
    surface.onfinish = finish;
    const onMotionChange = () => { if (media.matches) finish(); };
    media.addEventListener("change", onMotionChange);
    return () => {
      surface.cancel();
      height.cancel();
      media.removeEventListener("change", onMotionChange);
    };
  }, [phase]);

  React.useEffect(() => {
    if (phase === "open") controllerRef.current?.restoreView?.();
  }, [phase]);

  function toggle() {
    keepOpen();
    if (phase === "opening" || phase === "closing") {
      const style = getComputedStyle(panelRef.current);
      transitionRef.current = {
        height: frameRef.current.getBoundingClientRect().height,
        transform: style.transform,
        opacity: style.opacity,
      };
    }
    if (isExpanded && panelRef.current?.contains(document.activeElement)) buttonRef.current?.focus({ preventScroll: true });
    setPhase(isExpanded ? "closing" : "opening");
  }

  return {
    buttonRef, frameRef, panelRef, phase, isExpanded, isMounted, isCountingDown,
    keepOpen, toggle, onControllerReady, onMapRenderMetaChange,
  };
}
